<template>
  <b-modal
    v-model="show"
    title="Distribuir fichas - Automático"
    size="md"
    @hidden="onClose"
    :no-close-on-backdrop="true"
  >
    <div v-if="loading" class="text-center">
      <b-spinner></b-spinner>
      <p>Carregando dados...</p>
    </div>

    <div v-else>
      <!-- PASSO 1: Quantidade -->
      <div v-if="step === 1">
        <p class="mb-3">Quantas fichas você deseja distribuir?</p>

        <div class="alert alert-info">
          <i class="mdi mdi-information"></i>
          Total de fichas disponíveis: <strong>{{ availableCount }}</strong><br>
          <small class="text-muted">Tickets novos com status "A Definir"</small>
        </div>

        <div class="form-group">
          <label>Quantidade de fichas</label>
          <input
            type="number"
            class="form-control"
            v-model.number="quantity"
            :max="availableCount"
            min="1"
            placeholder="Ex: 10"
          />
          <small class="text-muted">Máximo: {{ availableCount }} fichas</small>
        </div>
      </div>

      <!-- PASSO 2: Selecionar Operadores -->
      <div v-if="step === 2">
        <p class="mb-3">A qual operador deseja atribuir as fichas?</p>

        <div class="operator-list">
          <div
            v-for="operator in operators"
            :key="operator.id"
            class="operator-item p-3 mb-2 border rounded cursor-pointer"
            :class="{'selected': selectedOperators.includes(operator.id)}"
            @click="toggleOperator(operator.id)"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-0">{{ operator.name }}</h6>
                <small class="text-muted">Fichas atribuídas: {{ operator.assigned_count || 0 }}</small>
              </div>
              <div v-if="selectedOperators.includes(operator.id)">
                <i class="mdi mdi-check-circle text-primary" style="font-size: 24px;"></i>
              </div>
            </div>
          </div>
        </div>

        <div v-if="operators.length === 0" class="alert alert-warning">
          Nenhum operador disponível
        </div>

        <!-- RESUMO -->
        <div v-if="selectedOperators.length > 0" class="alert alert-success mt-3">
          <strong>{{ quantity }} fichas</strong> serão distribuídas entre
          <strong>{{ selectedOperators.length }} operador(es)</strong>
          <br>
          <small>
            Cada operador receberá aproximadamente
            <strong>{{ Math.floor(quantity / selectedOperators.length) }} fichas</strong>
          </small>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <button class="btn btn-secondary" @click="onClose" v-if="step === 1">Cancelar</button>
      <button class="btn btn-secondary" @click="step = 1" v-if="step === 2">
        <i class="mdi mdi-arrow-left"></i> Voltar
      </button>
      <button
        class="btn btn-primary"
        @click="nextStep"
        v-if="step === 1"
        :disabled="!quantity || quantity > availableCount"
      >
        Próximo <i class="mdi mdi-arrow-right"></i>
      </button>
      <button
        class="btn btn-primary"
        @click="distribute"
        v-if="step === 2"
        :disabled="selectedOperators.length === 0 || distributing"
      >
        <span v-if="distributing">
          <b-spinner small></b-spinner> Distribuindo...
        </span>
        <span v-else>
          <i class="mdi mdi-check"></i> Distribuir
        </span>
      </button>
    </template>
  </b-modal>
</template>

<script>
import services from '../services'
import { errorMessage, successMessage } from '@/components/alertsMessages'

export default {
  name: 'ModalAssignAuto',
  props: {
    value: Boolean
  },
  data () {
    return {
      show: false,
      loading: false,
      distributing: false,
      step: 1,
      quantity: null,
      availableCount: 0,
      operators: [],
      selectedOperators: []
    }
  },
  watch: {
    value (val) {
      this.show = val
      if (val) {
        this.resetModal()
        this.loadData()
      }
    },
    show (val) {
      this.$emit('input', val)
    }
  },
  methods: {
    resetModal () {
      this.step = 1
      this.quantity = null
      this.selectedOperators = []
    },

    async loadData () {
      this.loading = true
      try {
        // Buscar fichas disponíveis (status = 1 "A Definir")
        const availableRes = await services.getAvailableParticipants({ limit: 1 })
        const operatorsRes = await services.getOperators()

        this.availableCount = availableRes.data.meta?.pagination?.total || 0
        this.operators = operatorsRes.data.data || []
      } catch (error) {
        errorMessage('Erro ao carregar dados')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    nextStep () {
      if (!this.quantity || this.quantity > this.availableCount) {
        errorMessage('Quantidade inválida')
        return
      }
      this.step = 2
    },

    toggleOperator (id) {
      const index = this.selectedOperators.indexOf(id)
      if (index > -1) {
        this.selectedOperators.splice(index, 1)
      } else {
        this.selectedOperators.push(id)
      }
    },

    async distribute () {
      if (this.selectedOperators.length === 0) {
        errorMessage('Selecione ao menos um operador')
        return
      }

      this.distributing = true
      try {
        await services.assignParticipantsAuto({
          quantity: this.quantity,
          operator_ids: this.selectedOperators
        })

        successMessage(`${this.quantity} fichas distribuídas com sucesso!`)
        this.$emit('distributed')
        this.onClose()
      } catch (error) {
        errorMessage(error.response?.data?.message || 'Erro ao distribuir fichas')
      } finally {
        this.distributing = false
      }
    },

    onClose () {
      this.show = false
      this.resetModal()
    }
  }
}
</script>

<style scoped>
.operator-list {
  max-height: 300px;
  overflow-y: auto;
}

.operator-item {
  transition: all 0.2s;
  cursor: pointer;
}

.operator-item:hover {
  background-color: #f8f9fa;
  border-color: #0d6efd !important;
}

.operator-item.selected {
  background-color: #e7f3ff;
  border-color: #0d6efd !important;
  border-width: 2px !important;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
